import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { SignupFormValues } from "@/shared/lib/validation/user-schemas";
import { RepresentativeSearchDialog } from "./representative-search-dialog";
import { COUNTRIES } from "@/shared/constants";
import { useTranslations } from "next-intl";

export function ProfileStep() {
  const form = useFormContext<SignupFormValues>();
  const t = useTranslations();
  const [selectedCountry, setSelectedCountry] = useState("BR"); // 브라질 기본값

  const currentCountry = COUNTRIES.find((c) => c.code === selectedCountry);

  const handleCountryChange = (countryCode: string) => {
    setSelectedCountry(countryCode);
    form.setValue("countryCode", countryCode);
  };

  return (
    <div className="tb:pt-25 tb:pb-40 pc:max-w-[620px] w-full py-20">
      <h3 className="heading-3-bold tb:mb-10 mb-8 text-center text-gray-900">{t("f_ei_title")}</h3>
      <div className="pc:space-y-8 space-y-5">
        {/* 국가 선택 */}
        <FormField
          control={form.control}
          name="countryCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="body-md-bold text-gray-900">{t("f_ei_lable_nation")}</FormLabel>
              <Select defaultValue={field.value} onValueChange={handleCountryChange}>
                <FormControl>
                  <SelectTrigger
                    variant="public"
                    className="tb:[&_svg]:size-5 w-full [&_svg]:size-4 [&_svg]:text-gray-900"
                  >
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent variant="public">
                  <SelectGroup>
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country.code} value={country.code} variant="public" className="body-md-regular">
                        {t(country.name)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* 연락처 */}
        <FormField
          control={form.control}
          rules={{ pattern: { value: /^[0-9]+$/, message: "연락처는 숫자만 입력해주세요." } }}
          name="phoneNumber"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel className="body-md-bold text-gray-900" htmlFor="phoneNumber">
                {t("f_ei_lable_phone")}
              </FormLabel>
              <FormControl>
                <div className="tb:gap-2 flex gap-[6px]">
                  <div className="tb:rounded-10 body-md-regular flex items-center rounded-[8px] border border-gray-200 bg-white px-4 text-gray-900">
                    {currentCountry?.phoneCode}
                  </div>
                  <Input
                    {...field}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      field.onChange(value);
                    }}
                    id="phoneNumber"
                    variant="public"
                    placeholder={t("f_ei_placeholder_phone")}
                    aria-invalid={!!fieldState.error}
                    type="tel"
                    inputMode="numeric"
                    maxLength={50}
                  />
                </div>
              </FormControl>
              <FormMessage className="body-sm-regular text-red-500" />
            </FormItem>
          )}
        />

        {/* 회사명 */}
        <FormField
          control={form.control}
          name="companyName"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel className="body-md-bold text-gray-900" htmlFor="companyName">
                  {t("f_ei_lable_name")}
                </FormLabel>
                <FormControl>
                  <Input
                    id="companyName"
                    variant="public"
                    placeholder={t("f_ei_placeholder_name")}
                    {...field}
                    maxLength={50}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <div className="tb:space-y-2 space-y-[6px]">
          <FormLabel className="body-md-bold text-gray-900">{t("f_ei_lable_ads")}</FormLabel>
          {/* 우편번호 */}
          <div className="tb:space-y-3 space-y-2">
            <FormField
              control={form.control}
              name="zip"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="ZIP code (CEP)"
                      value={field.value || ""}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, "");
                        field.onChange(value);
                      }}
                      variant="public"
                      maxLength={8}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* 주소 상세 정보 */}

            <FormField
              control={form.control}
              name="streetAddress"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="Street address"
                      value={field.value || ""}
                      onChange={field.onChange}
                      variant="public"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="houseNumber"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="House Number"
                      value={field.value || ""}
                      onChange={field.onChange}
                      variant="public"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="complement"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="Complement (optional)"
                      value={field.value || ""}
                      onChange={field.onChange}
                      variant="public"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="area"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input placeholder="Area" value={field.value || ""} onChange={field.onChange} variant="public" />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input placeholder="City" value={field.value || ""} onChange={field.onChange} variant="public" />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input placeholder="State" value={field.value || ""} onChange={field.onChange} variant="public" />
                  </FormControl>
                </FormItem>
              )}
            />

            {(form.formState.errors.zip ||
              form.formState.errors.streetAddress ||
              form.formState.errors.houseNumber ||
              form.formState.errors.area ||
              form.formState.errors.city ||
              form.formState.errors.state) && <div className="text-sm text-red-500">{t("f_ei_err_ads")}</div>}
          </div>
        </div>
        {/* 담당자 이름 */}
        <FormField
          control={form.control}
          name="adminName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="body-md-bold text-gray-900">{t("f_ei_lable_rep")}</FormLabel>
              <FormControl>
                <div className="flex w-full items-center gap-2">
                  <div
                    className={`tb:rounded-10 body-md-regular tb:h-[52px] pointer-events-none flex h-[46px] w-full items-center rounded-[8px] border border-gray-200 bg-white px-4 ${
                      field.value ? "text-gray-900" : "text-gray-400"
                    }`}
                  >
                    {field.value || t("f_ei_placeholder_rep")}
                  </div>
                  <RepresentativeSearchDialog />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" variant="primary" size="lg" className="mt-5 w-full">
          {t("f_ei_btn_done")}
        </Button>
      </div>
    </div>
  );
}
